/**
 * <s-newsletter-popup> — decides when the first-visit email signup dialog opens.
 *
 * Opens the child <dialog> once per visitor after `delay` seconds, never again
 * after it is closed, and never for someone who has already signed up from any
 * signup form on the site. When the popup's own form posts back successfully
 * (URL has customer_posted=true and the #newsletter-popup anchor), it reopens
 * immediately so the visitor sees the thank-you message and discount code.
 *
 * Attributes:
 *   delay       — seconds to wait before opening on a first visit (default 6)
 *   form-anchor — id of the popup form, used to detect its successful post
 */
const DISMISSED_KEY = 'sassard:newsletter-popup:dismissed';
const SIGNED_UP_KEY = 'sassard:newsletter-popup:signed-up';

class SNewsletterPopup extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const params = new URLSearchParams(window.location.search);
    const postedHere = params.get('customer_posted') === 'true';
    const anchor = this.getAttribute('form-anchor');
    const popupPosted = postedHere && anchor && window.location.hash === `#${anchor}`;

    if (popupPosted) {
      this.#store(SIGNED_UP_KEY);
      dialog.showModal();
      return;
    }

    if (postedHere) this.#store(SIGNED_UP_KEY);
    if (this.#read(SIGNED_UP_KEY) || this.#read(DISMISSED_KEY)) return;

    dialog.addEventListener('close', () => this.#store(DISMISSED_KEY), { signal: this.#ac.signal });

    const delay = Number(this.getAttribute('delay') || 6) * 1000;
    this.#timer = window.setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog[open]')) dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #read(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #store(key) {
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      /* Storage can be unavailable in private modes; the popup then simply shows per visit. */
    }
  }
}

if (!customElements.get('s-newsletter-popup')) {
  customElements.define('s-newsletter-popup', SNewsletterPopup);
}
